/**
 * Brand Helper Utilities
 * Provides brand-aware asset path resolution for the frontend
 */

import xhelosConfig from '@/brands/xhelos/brand.config.js';
import alberathConfig from '@/brands/alberath/brand.config.js';
import { getCurrentBreakpoint } from './responsive.js';

/**
 * Get the current game brand from environment
 * @returns {string} Current brand name
 * @throws {Error} If VITE_GAME_BRAND is not set
 */
export function getCurrentBrand() {
  const brand = import.meta.env.VITE_GAME_BRAND;

  if (!brand) {
    throw new Error('VITE_GAME_BRAND environment variable is not set');
  }

  return brand;
}

/**
 * Get brand-aware asset path
 * Prepends the current brand directory to the asset path
 *
 * @param {string} path - Asset path without leading slash (e.g., 'images/units/swordsman.svg')
 * @returns {string} Full path with brand prefix (e.g., '/xhelos/images/units/swordsman.svg')
 * @throws {Error} If VITE_GAME_BRAND is not set
 *
 * @example
 * getAssetPath('images/units/swordsman.svg') // '/xhelos/images/units/swordsman.svg'
 * getAssetPath('assets/logo.png') // '/xhelos/assets/logo.png'
 */
export function getAssetPath(path) {
  const brand = getCurrentBrand();

  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;

  return `/${brand}/${cleanPath}`;
}

/**
 * Get brand configuration
 * Returns the brand config for the current brand
 *
 * @returns {Object} Brand configuration object
 * @throws {Error} If brand config cannot be loaded
 *
 * @example
 * const config = getBrandConfig();
 * console.log(config.map.tileSize); // 42 for xhelos, 56 for alberath
 */
export function getBrandConfig() {
  const brand = getCurrentBrand();

  const configs = {
    xhelos: xhelosConfig,
    alberath: alberathConfig,
  };

  const config = configs[brand];

  if (!config) {
    throw new Error(`Brand config not found for: ${brand}`);
  }

  return config;
}

/**
 * Get tile size for current screen breakpoint
 * Returns the appropriate tile size based on viewport width
 *
 * @param {string} breakpoint - Optional breakpoint override ('wide', 'desktop', 'tablet', 'mobile', 'portrait')
 * @returns {number} Tile size in pixels
 * @throws {Error} If brand config is missing tile size for breakpoint
 *
 * @example
 * // Get tile size for current viewport
 * const size = getTileSize(); // Returns size based on window.innerWidth
 *
 * // Get tile size for specific breakpoint
 * const mobileSize = getTileSize('mobile'); // 32 for xhelos, 36 for alberath
 * const wideSize = getTileSize('wide'); // 48 for xhelos, 64 for alberath
 */
export function getTileSize(breakpoint) {
  const config = getBrandConfig();
  const bp = breakpoint || getCurrentBreakpoint();

  switch (bp) {
    case 'portrait':
      return config.map.tileSizePortrait;
    case 'mobile':
      return config.map.tileSizeMobile;
    case 'tablet':
      return config.map.tileSizeTablet;
    case 'desktop':
      return config.map.tileSize;
    case 'wide':
      return config.map.tileSizeWide;
    default:
      return config.map.tileSize;
  }
}

/**
 * Get formatted game version string
 * Combines brand name, version prefix, and version number
 *
 * @returns {string} Formatted version string (e.g., "Alberath v0.6.2" or "Xhelos v2.6.2")
 *
 * @example
 * // With VITE_GAME_BRAND=alberath and VITE_GAME_VERSION=6.2
 * getGameVersion() // "Alberath v0.6.2"
 *
 * // With VITE_GAME_BRAND=xhelos and VITE_GAME_VERSION=6.2
 * getGameVersion() // "Xhelos v2.6.2"
 */
export function getGameVersion() {
  const config = getBrandConfig();
  const version = import.meta.env.VITE_GAME_VERSION || '0.0';

  return `${config.name} v${config.versionPrefix}.${version}`;
}
